import { Drawer } from '@halo/ui-react';
import { Center, Field, Flex, Input, Stack, Switch, Text, Box, Button } from '@chakra-ui/react';
import { Avatar, BackIcon } from '@halo/ui-chat-react';
import type { User } from '@halo/chat-sdk';
import BackUpIcon from '@/assets/images/contacts/svg/backUp.svg?react';
import DeleteIcon from '@/assets/images/contacts/svg/delete.svg?react';
import { useContactsQuery } from '@/scripts/hooks';
import { InfoItem } from './InfoItem';
import type { SetMyContactDialogForm } from './index';

export type ContactDrawerProps = {
  loading: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onOK: (form: SetMyContactDialogForm) => Promise<void>;
  onClose?: () => void;
  user: User | null;
  myContactSettingOnlyEdit?: boolean;
  syncBackupContactChecked: boolean;
  onDelOpen: () => void;
};

function EditContract({
  isOpen,
  setIsOpen,
  myContactSettingOnlyEdit,
  user,
  syncBackupContactChecked,
  onDelOpen,
  onOK,
}: ContactDrawerProps) {
  const { t } = useTranslation();
  const { isContact } = useContactsQuery(user?.user_id ?? '');
  const [remarkFirstName, setRemarkFirstName] = useState('');
  const [remarkLastName, setRemarkLastName] = useState('');

  useEffect(() => {
    setRemarkFirstName(user?.remark_first_name || '');
    setRemarkLastName(user?.remark_last_name || '');
  }, [user]);

  return (
    <Drawer.Root closeOnInteractOutside={false} modal={false} open={isOpen}>
      <Drawer.Positioner zIndex={1500}>
        <Drawer.Content w="480px" maxW="100%" zIndex={1500}>
          <Drawer.Header
            className="Header"
            pt="5"
            px="5"
            pb="2.5"
            justifyContent="space-between"
            bg={'bg.subtle'}
          >
            <Drawer.Title fontSize="md" fontWeight="semibold" asChild>
              <Flex
                gap={2}
                alignItems={'center'}
                cursor="pointer"
                onClick={() => setIsOpen(false)}
              >
                <BackIcon />
                <Text>
                  {myContactSettingOnlyEdit ? t('action.edit') : t('action.addContact')}
                </Text>
              </Flex>
            </Drawer.Title>
          </Drawer.Header>
          <Drawer.Body className="scrollHover" p={0} bg={'bg.subtle'}>
            <Stack gap="2" px={5}>
              <Center p="4">
                <Avatar
                  width={86}
                  height={86}
                  src={user?.avatar}
                  size="2xl"
                  activeShake={false}
                />
              </Center>
              <Field.Root>
                <Input
                  variant="subtle"
                  bg="bg"
                  size="sm"
                  fontWeight="semibold"
                  maxLength={32}
                  borderRadius="lg"
                  borderColor="border.input"
                  placeholder={
                    user?.first_name
                      ? t('chat.hasFirstNamePlaceholder', { name: user?.first_name })
                      : t('chat.noFirstNamePlaceholder')
                  }
                  value={remarkFirstName}
                  onChange={(e) => setRemarkFirstName(e.target.value)}
                />
              </Field.Root>
              <Field.Root>
                <Input
                  variant="subtle"
                  bg="bg"
                  size="sm"
                  fontWeight="semibold"
                  maxLength={32}
                  borderRadius="lg"
                  borderColor="border.input"
                  placeholder={
                    user?.last_name
                      ? t('chat.hasLastNamePlaceholder', { name: user?.last_name })
                      : t('chat.noLastNamePlaceholder')
                  }
                  value={remarkLastName}
                  onChange={(e) => setRemarkLastName(e.target.value)}
                />
              </Field.Root>
              {(!myContactSettingOnlyEdit || isContact) && (
                <InfoItem
                  icon={<BackUpIcon />}
                  label={t('chat.backupContacts')}
                  extra={
                    <Switch.Root checked={syncBackupContactChecked} disabled>
                      <Switch.HiddenInput />
                      <Switch.Control />
                    </Switch.Root>
                  }
                />
              )}
              {isContact && (
                <InfoItem
                  icon={<DeleteIcon />}
                  label={t('chat.deleteContact')}
                  onClick={onDelOpen}
                />
              )}
            </Stack>

            <Box p={5} mt={6}>
              <Button
                borderRadius="lg"
                width={'full'}
                onClick={() =>
                  onOK({
                    remarkFirstName: remarkFirstName.trim(),
                    remarkLastName: remarkLastName.trim(),
                  })
                }
              >
                {t('buttons.save')}
              </Button>
            </Box>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}

export default EditContract;
